import React, { useEffect, useMemo, useState } from 'react'

import { Breakpoints } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps, Flex, Stack } from '@mantine/core'

import CarouselCard from 'components/atoms/CarouselCardV2/'
import { Container } from 'components/atoms/container'
import GroupItems from 'components/atoms/groupItems'
import { subotInlineFirstNode } from 'components/organisms/subotInline/subotInline.config'
import {
  CarePathColorPattern,
  CarePathColorType,
} from 'components/templates/carePath/constant'
import { SpotlightMultiRiskScreenerSession } from 'interfaces/types'
import { getChildrenArray } from 'utils/getChildrenArray'

import useStyles from './index.styles'
import {
  RiskScreenerEmbedItem,
  RiskScreenerItem,
} from './riskScreenerEmbedItem'

type MultiRiskScreenerEmbedProps = {
  sessionProps: SpotlightMultiRiskScreenerSession
  colorShade: CarePathColorPattern
  applyH1?: boolean
} & BoxProps

const MultiRiskScreenerEmbed = ({
  sessionProps,
  className,
  classNames,
  styles,
  colorShade,
  applyH1,
  ...boxProps
}: MultiRiskScreenerEmbedProps) => {
  const {
    listSubot = [],
    horizontalDisplay: carouselItemDisplayAsInline,
  } = sessionProps
  const [listRes, setListRes] = useState<RiskScreenerItem[]>([])
  const { classes, cx, theme } = useStyles(
    {
      layout:
        carouselItemDisplayAsInline && listRes.length > 1
          ? 'vertical'
          : 'horizontal',
      carouselItemLayout: carouselItemDisplayAsInline ? 'inline' : 'group',
      itemLength: listRes.length,
    },
    {
      name: 'MultiRiskScreenerEmbed',
      classNames,
      styles,
    },
  )
  const colorMapping: CarePathColorType[] = [
    'primary',
    'secondary',
    'tertiary',
    'quaternary',
  ]
  const configs = useMemo(() => {
    return {
      defaultItemsPerRow: carouselItemDisplayAsInline ? 1 : 2,
      defaultRowsPerSlide: carouselItemDisplayAsInline ? 1 : 2,
      slideSize: carouselItemDisplayAsInline
        ? `${100 / Math.min(listRes.length, 4)}%`
        : '100%',
    }
  }, [carouselItemDisplayAsInline, listRes])

  const fetchListSubot = async () => {
    try {
      const res = await Promise.all(
        listSubot.map((i) => subotInlineFirstNode(i.id)),
      )
      const listRes = res
        .map(
          (i, idx) =>
            i?._data && {
              bgColor: listSubot[idx].backgroundColor,
              thumbnail: i?._data?.landing_page_image || '',
              title: i?._data?.landing_page_title || '',
              reviewerName: i?._data?.landing_page_reviewer || '',
              reviewerAvatar: i?._data?.landing_page_avatar || '',
              reviewerHospital: i?._data?.landing_page_hospital_name || '',
              reviewerSpecialty: i?._data?.landing_page_speciality || '',
              langdingPageUrlPrefix: i?._data?.landing_page_url_prefix || '',
              landingPageUrlSlug: i?._data?.landing_page_url_slug || '',
            },
        )
        .filter((i) => i.title !== '')
      setListRes(listRes)
    } catch {}
  }

  useEffect(() => {
    if (listSubot.length > 0) fetchListSubot()
  }, [])

  return listRes.length > 0 ? (
    <Container>
      <Box className={cx(classes.root, className)} {...boxProps}>
        {(sessionProps.title || sessionProps.description) && (
          <Box className={classes.info}>
            <Stack className={classes.infoInner}>
              {sessionProps.title && (
                <Text
                  size="h2"
                  as={applyH1 ? 'h1' : 'h2'}
                  className={classes.title}
                >
                  {sessionProps.title}
                </Text>
              )}
              {sessionProps.description && (
                <Box>
                  <Text size="p4" as="div">
                    <Box
                      className={classes.editorContent}
                      dangerouslySetInnerHTML={{
                        __html: sessionProps.description,
                      }}
                    />
                  </Text>
                </Box>
              )}
            </Stack>
          </Box>
        )}
        <Box className={classes.listing}>
          <CarouselCard
            containScroll="trimSnaps"
            className={classes.carousel}
            slideSize={configs.slideSize}
            breakpoints={[
              {
                maxWidth: Breakpoints.BREAK_POINT_TABLET,
                slideSize: '100%',
              },
            ]}
            slideGap={24}
            align={'start'}
            withIndicators={!carouselItemDisplayAsInline}
            styles={(theme) => ({
              indicators: {
                position: 'static',
                paddingTop: 24,
                [theme.fn.smallerThan('sm')]: {
                  display: 'none',
                },
              },
            })}
          >
            <Stack
              spacing={24}
              sx={{
                ...(carouselItemDisplayAsInline && {
                  width: '100%',
                }),
                position: 'relative',
                '.arrow': {
                  position: 'absolute',
                  top: '50%',
                  transform: 'translate(0, -50%)',
                  border: `1px solid ${theme.colors.gray[1]}`,
                  width: 40,
                  height: 40,
                  boxShadow:
                    '0px 0px 1px 0px #0000000A,0px 2px 8px 0px #0000000A,0px 10px 16px 0px #0000000A',
                  '&.arrow-prev': {
                    left: -64,
                  },
                  '&.arrow-next': {
                    right: -64,
                  },
                },
              }}
            >
              <CarouselCard.Body>
                <GroupItems
                  breakpoints={{
                    base: {
                      itemsPerGroup: listRes.length,
                      GroupWrapper: (children) => (
                        <CarouselCard.Slide>
                          <Stack spacing={16}>{children}</Stack>
                        </CarouselCard.Slide>
                      ),
                    },
                    md: {
                      itemsPerGroup: carouselItemDisplayAsInline ? 1 : 4,
                      GroupWrapper: (children) => (
                        <CarouselCard.Slide>
                          {carouselItemDisplayAsInline ? (
                            children
                          ) : (
                            <Flex
                              gap={24}
                              wrap="wrap"
                              className={'slide-item'}
                              data-item={getChildrenArray(children).length}
                            >
                              {children}
                            </Flex>
                          )}
                        </CarouselCard.Slide>
                      ),
                    },
                  }}
                  items={listRes.map((i, idx) => (
                    <RiskScreenerEmbedItem
                      className={classes.embedItem}
                      color={
                        i.bgColor ||
                        colorShade[colorMapping[idx % colorMapping.length]]
                      }
                      key={String(idx)}
                      {...i}
                    />
                  ))}
                  ItemWrapper={(children) => (
                    <Box className={classes.item}>{children}</Box>
                  )}
                />
              </CarouselCard.Body>
              <CarouselCard.Control
                className="arrow arrow-prev"
                direction="prev"
              />
              <CarouselCard.Control
                className="arrow arrow-next"
                direction="next"
              />
            </Stack>
          </CarouselCard>
        </Box>
      </Box>
    </Container>
  ) : null
}

export { MultiRiskScreenerEmbed }
